import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import {
  IQuartzJobEntry,
  IQuartzSchedulerInfo,
} from "../../../@types/admin/AdminQuartzJobs";
import { TableSorted } from "../../../components/TableSorted";
import {
  quartzJobsOverviewKey,
  useQuartzJobsOverview,
} from "../../../hooks/queries/admin/quartzJobs";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminSelectField,
  CheckboxField,
  ErrorMessage,
  FilterButton,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageDescription,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  StatCard,
  StatLabel,
  StatusBadge,
  StatValue,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import {
  CellSubtext,
  FilterField,
  FilterRow,
  HeaderMeta,
  StatsRow,
  WarningCount,
} from "./AdminQuartzJobs.styles";
import { QuartzJobDetailModal } from "./QuartzJobDetailModal";
import {
  EMPTY_QUARTZ_FILTERS,
  filterEntries,
  hasActiveFilter,
  hospitalLabel,
  IQuartzFilterState,
  relativeTime,
  scheduleLabel,
  stateLabel,
  stateTone,
  targetLabel,
  tenantLabel,
  wardOptionsFor,
} from "./quartzJobsPresentation";

const EMPTY_ENTRIES: IQuartzJobEntry[] = [];

/** One muted line of scheduler facts for the header; the instance id goes in the tooltip. */
function schedulerMetaLine(
  scheduler: IQuartzSchedulerInfo,
  pausedTriggerGroups: string[]
) {
  const parts = [scheduler.schedulerName];
  if (scheduler.deploymentRegion)
    parts.push(`region ${scheduler.deploymentRegion}`);
  if (scheduler.isClustered) parts.push("clustered");
  if (pausedTriggerGroups.length > 0) {
    parts.push(`paused groups: ${pausedTriggerGroups.join(", ")}`);
  }
  parts.push(`snapshot ${getLongFormattedDate(scheduler.capturedAtUtc)}`);
  return parts.join(" · ");
}

function schedulerStatusLabel(scheduler: IQuartzSchedulerInfo | undefined) {
  if (!scheduler) return "Unknown";
  if (scheduler.isShutdown) return "Shut down";
  if (scheduler.inStandbyMode) return "Standby";
  return scheduler.isStarted ? "Running" : "Not started";
}

/**
 * Scheduled Jobs (Quartz) admin page.
 *
 * Every Quartz job and trigger in this region's scheduler in one table — the
 * static regional jobs (alerts, scheduled actions, integration queues), one
 * trigger per published Inactivity / Date-event workflow, and one per ward
 * with a Dexcom / Google Health / JHE sync — each resolved to the workflow,
 * ward, hospital and tenant it serves, with warnings where something looks
 * off (a trigger for a deleted or unpublished workflow, a paused or errored
 * trigger, a job registered but disabled by configuration, a sync trigger
 * for a ward whose integration flag is off). Click a row for the full
 * detail. Read-only by design: the scheduler is changed by publishing
 * workflows and editing ward configuration, not from here.
 *
 * The API returns the whole overview in one call; the filters here narrow it
 * client-side, so they respond instantly and the dropdown options never hide
 * their own alternatives.
 */
export function AdminQuartzJobs() {
  document.title = "Scheduled jobs - Admin";

  const { openModal } = useModal();
  const { overview, isLoading, isError, status } = useQuartzJobsOverview();
  const [filters, setFilters] =
    useState<IQuartzFilterState>(EMPTY_QUARTZ_FILTERS);

  const setFilter = <K extends keyof IQuartzFilterState>(
    key: K,
    value: IQuartzFilterState[K]
  ) => setFilters((previous) => ({ ...previous, [key]: value }));

  // Changing the hospital narrows the ward list, so a ward from another
  // hospital must not linger in the filter.
  useEffect(() => {
    setFilters((previous) => {
      if (!previous.wardId || !previous.hospitalGroupId) return previous;
      const stillValid = wardOptionsFor(
        overview,
        previous.hospitalGroupId
      ).some((ward) => String(ward.id) === previous.wardId);
      return stillValid ? previous : { ...previous, wardId: "" };
    });
  }, [filters.hospitalGroupId, overview]);

  const entries = overview?.entries ?? EMPTY_ENTRIES;
  const filtered = useMemo(
    () => filterEntries(entries, filters),
    [entries, filters]
  );

  const { items, pagination, setPage, setPageSize } = useMockPagination({
    data: filtered,
  });

  // The cards describe what the table shows: with a filter on, both narrow together.
  const counts = useMemo(
    () => ({
      triggers: filtered.filter((entry) => entry.hasTrigger).length,
      jobsWithoutTrigger: filtered.filter((entry) => !entry.hasTrigger).length,
      executing: filtered.filter((entry) => entry.isExecuting).length,
      withWarnings: filtered.filter((entry) => entry.warnings.length > 0)
        .length,
    }),
    [filtered]
  );

  const handleRowClick = (entry: IQuartzJobEntry) => {
    openModal(<QuartzJobDetailModal entry={entry} />, {
      width: "min(1100px, 96vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IQuartzJobEntry, string>[] = useMemo(
    () => [
      {
        accessorKey: "category",
        header: "Category",
        enableSorting: true,
      },
      {
        accessorKey: "jobName",
        header: "Job",
        cell: ({ row }) => (
          <span>
            {row.original.jobName}
            <CellSubtext>{row.original.jobGroup}</CellSubtext>
          </span>
        ),
        enableSorting: true,
      },
      {
        id: "target",
        header: "Runs for",
        accessorFn: (entry) => targetLabel(entry),
        cell: ({ row }) => {
          const hospital = hospitalLabel(row.original);
          const tenant = tenantLabel(row.original);
          const sub = [hospital, tenant].filter(Boolean).join(" · ");
          return (
            <span>
              {targetLabel(row.original)}
              {sub ? <CellSubtext>{sub}</CellSubtext> : null}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: "schedule",
        header: "Schedule",
        accessorFn: (entry) => scheduleLabel(entry),
        enableSorting: true,
      },
      {
        id: "state",
        header: "State",
        accessorFn: (entry) => stateLabel(entry),
        cell: ({ row }) => (
          <StatusBadge status={stateTone(row.original)}>
            {stateLabel(row.original)}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "nextFireTimeUtc",
        header: "Next fire",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.nextFireTimeUtc)}
            <CellSubtext>
              {relativeTime(row.original.nextFireTimeUtc)}
            </CellSubtext>
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "previousFireTimeUtc",
        header: "Last fire",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.previousFireTimeUtc)}
            <CellSubtext>
              {relativeTime(row.original.previousFireTimeUtc)}
            </CellSubtext>
          </span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "region",
        header: "Region",
        enableSorting: true,
      },
      {
        id: "warnings",
        header: "Warnings",
        // The label, not the count: a zero count is falsy and TableSorted
        // would render its own "-" — which is fine, but sorting on the
        // string keeps the rows with warnings together.
        accessorFn: (entry) =>
          entry.warnings.length > 0 ? `${entry.warnings.length}` : "",
        cell: ({ row }) => (
          <WarningCount title={row.original.warnings.join("\n")}>
            {row.original.warnings.length}
          </WarningCount>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  const scheduler = overview?.scheduler;
  const wardOptions = wardOptionsFor(overview, filters.hospitalGroupId);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Scheduled jobs</PageTitle>
          <PageInfoTooltip title="Scheduled jobs (Quartz)">
            Every Quartz job and trigger in this region&apos;s scheduler: the
            static regional jobs, one trigger per published Inactivity or Date
            event workflow, and one per ward with a Dexcom, Google Health or JHE
            sync. Each row is resolved to the workflow, ward, hospital and
            tenant it serves, and carries warnings when it looks stale, paused,
            errored or disabled by configuration. Click a row for the full
            detail. Read-only — publish a workflow or edit a ward configuration
            to change the scheduler. Refreshes every minute.
          </PageInfoTooltip>
        </PageTitleGroup>
        {scheduler ? (
          <HeaderMeta title={`Instance ${scheduler.schedulerInstanceId}`}>
            {schedulerMetaLine(scheduler, overview?.pausedTriggerGroups ?? [])}
          </HeaderMeta>
        ) : null}
      </PageHeader>

      {isError ? (
        <ErrorMessage>
          {status === 403
            ? "Your account doesn't have access to the scheduled jobs overview."
            : "Couldn't load the scheduled jobs. Please try again."}
        </ErrorMessage>
      ) : null}

      {isLoading ? <TableLoadingState label="Reading the scheduler…" /> : null}

      {overview && !isError ? (
        <>
          <StatsRow>
            <StatCard>
              <StatLabel>Scheduler</StatLabel>
              <StatValue>
                <StatusBadge
                  status={
                    scheduler?.isStarted && !scheduler.inStandbyMode
                      ? "active"
                      : "error"
                  }
                >
                  {schedulerStatusLabel(scheduler)}
                </StatusBadge>
              </StatValue>
              {scheduler?.runningSinceUtc ? (
                <CellSubtext>
                  since {getLongFormattedDate(scheduler.runningSinceUtc)}
                </CellSubtext>
              ) : null}
            </StatCard>
            <StatCard>
              <StatLabel>Triggers</StatLabel>
              <StatValue>{counts.triggers}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Jobs without a trigger</StatLabel>
              <StatValue>{counts.jobsWithoutTrigger}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Running now</StatLabel>
              <StatValue>{counts.executing}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>With warnings</StatLabel>
              <StatValue>{counts.withWarnings}</StatValue>
            </StatCard>
          </StatsRow>

          <FiltersContainer>
            <SearchInput
              aria-label="Search scheduled jobs"
              placeholder="Search by job, trigger, workflow, ward, hospital, tenant or cron..."
              value={filters.search}
              onChange={(event) => setFilter("search", event.target.value)}
            />
            <FiltersActions>
              <CheckboxField>
                <input
                  type="checkbox"
                  checked={filters.onlyWithWarnings}
                  onChange={(event) =>
                    setFilter("onlyWithWarnings", event.target.checked)
                  }
                />
                Only with warnings
              </CheckboxField>
              <FilterButton
                variant="secondary"
                disabled={!hasActiveFilter(filters)}
                onClick={() => setFilters(EMPTY_QUARTZ_FILTERS)}
              >
                Clear
              </FilterButton>
            </FiltersActions>

            <FilterRow>
              <FilterField>
                <FilterLabel htmlFor="quartz-category">Category</FilterLabel>
                <AdminSelectField
                  id="quartz-category"
                  value={filters.category}
                  onChange={(event) =>
                    setFilter("category", event.target.value)
                  }
                >
                  <option value="">All</option>
                  {overview.options.categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-group">Job group</FilterLabel>
                <AdminSelectField
                  id="quartz-group"
                  value={filters.jobGroup}
                  onChange={(event) =>
                    setFilter("jobGroup", event.target.value)
                  }
                >
                  <option value="">All</option>
                  {overview.options.jobGroups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-state">State</FilterLabel>
                <AdminSelectField
                  id="quartz-state"
                  value={filters.state}
                  onChange={(event) => setFilter("state", event.target.value)}
                >
                  <option value="">All</option>
                  {overview.options.states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-region">Region</FilterLabel>
                <AdminSelectField
                  id="quartz-region"
                  value={filters.region}
                  onChange={(event) => setFilter("region", event.target.value)}
                >
                  <option value="">All</option>
                  {overview.options.regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-tenant">Tenant</FilterLabel>
                <AdminSelectField
                  id="quartz-tenant"
                  value={filters.tenantId}
                  onChange={(event) =>
                    setFilter("tenantId", event.target.value)
                  }
                >
                  <option value="">All</option>
                  {overview.options.tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-hospital">Hospital</FilterLabel>
                <AdminSelectField
                  id="quartz-hospital"
                  value={filters.hospitalGroupId}
                  onChange={(event) =>
                    setFilter("hospitalGroupId", event.target.value)
                  }
                >
                  <option value="">All</option>
                  {overview.options.hospitalGroups.map((hospital) => (
                    <option key={hospital.id} value={String(hospital.id)}>
                      {hospital.name}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>

              <FilterField>
                <FilterLabel htmlFor="quartz-ward">Ward</FilterLabel>
                <AdminSelectField
                  id="quartz-ward"
                  value={filters.wardId}
                  onChange={(event) => setFilter("wardId", event.target.value)}
                >
                  <option value="">All</option>
                  {wardOptions.map((ward) => (
                    <option key={ward.id} value={String(ward.id)}>
                      {ward.name}
                    </option>
                  ))}
                </AdminSelectField>
              </FilterField>
            </FilterRow>
          </FiltersContainer>

          <TableContainer>
            <TableWrapper refreshKeys={[quartzJobsOverviewKey]}>
              <TableSorted
                columns={columns}
                data={items}
                onRowClick={handleRowClick}
                emptyStateText={
                  entries.length === 0
                    ? "The scheduler holds no jobs."
                    : "No scheduled jobs match these filters."
                }
              />
            </TableWrapper>
            <TablePagination
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </TableContainer>

          {entries.length === 0 ? (
            <PageDescription>
              The scheduler holds no jobs. In a deployed environment that means
              Quartz never registered its jobs — check the API logs.
            </PageDescription>
          ) : null}
        </>
      ) : null}
    </PageContainer>
  );
}
